import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemMedia,
  ItemTitle,
} from '@/components/ui/item';
import { ItemTypeDisplay } from '@/components/ItemType/ItemTypeDisplay';
import { PhotoGallery } from '@/components/PhotoGallery/PhotoGallery';
import { BrokenContainerPart } from '@/graphql/generatedTypes';
import i18n from '@/utils/i18n';
import { Routes } from '@/utils/routes';

import {
  brokenPartLabel,
  brokenReportContextLabel,
  formatBrokenReportDate,
  type BrokenContainerLineData,
  type BrokenContainerReportData,
} from './brokenContainerReportFormat';

const LINK_CLASS =
  'tw:text-foreground! tw:no-underline! tw:hover:underline! tw:underline-offset-4';

interface BrokenContainerReportProps {
  report: BrokenContainerReportData;
  lines?: BrokenContainerLineData[];
  showOrigin?: boolean;
  onClearLine?: (line: BrokenContainerLineData) => void;
}

export function BrokenContainerReport({
  report,
  lines = report.lines,
  showOrigin = false,
  onClearLine,
}: BrokenContainerReportProps) {
  const meta: ReactNode[] = [];
  if (report.created_by) {
    meta.push(
      i18n.t('BrokenContainers:reportedBy', { user: report.created_by }),
    );
  }
  if (showOrigin && report.store_name) meta.push(report.store_name);
  if (
    showOrigin &&
    report.merchant_order_id &&
    report.merchant_order_delivery_note_id != null
  ) {
    meta.push(
      <Link
        to={Routes.orderDetails(report.merchant_order_id)}
        className={LINK_CLASS}
      >
        {i18n.t('BrokenContainers:returnOrderLink', {
          deliveryNoteId: report.merchant_order_delivery_note_id,
        })}
      </Link>,
    );
  }

  return (
    <div className="tw:flex tw:flex-col tw:gap-3 tw:text-sm">
      <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-x-2 tw:gap-y-1">
        <Badge variant="subtle">
          {brokenReportContextLabel(report.context)}
        </Badge>
        <span>{formatBrokenReportDate(report.created_at)}</span>
        {meta.length > 0 && (
          <span className="tw:text-muted-foreground">
            {meta.map((part, index) => (
              <Fragment key={index}>
                {index > 0 && ' · '}
                {part}
              </Fragment>
            ))}
          </span>
        )}
      </div>

      {report.comment && (
        <div className="tw:rounded-lg tw:bg-muted/50 tw:px-3 tw:py-2 tw:whitespace-pre-wrap">
          {report.comment}
        </div>
      )}

      <ItemGroup className="tw:gap-2">
        {lines.map((line) => (
          <BrokenContainerLineItem
            key={line.id}
            line={line}
            onClear={onClearLine}
          />
        ))}
      </ItemGroup>

      {report.photos.length > 0 && (
        <div className="tw:flex tw:flex-col tw:gap-2">
          <div className="tw:font-medium">
            {i18n.t('BrokenContainers:photos', {
              count: report.photos.length,
            })}
          </div>
          <PhotoGallery photos={report.photos} />
        </div>
      )}
    </div>
  );
}

function BrokenContainerLineItem({
  line,
  onClear,
}: {
  line: BrokenContainerLineData;
  onClear?: (line: BrokenContainerLineData) => void;
}) {
  const typeName = line.container_type_name ?? `#${line.container_type_id}`;
  return (
    <Item variant="outline" size="sm" role="listitem">
      <ItemMedia>
        <ItemTypeDisplay
          alt={typeName}
          imageUrl={line.image_url}
          className="tw:size-10"
          showLargeImageOnHover
        />
      </ItemMedia>
      <ItemContent>
        <ItemTitle>
          <span className="tw:tabular-nums">
            {line.amount} × {typeName}
          </span>
          {line.part === BrokenContainerPart.QrCode && (
            <Badge variant="subtle">{brokenPartLabel(line.part)}</Badge>
          )}
        </ItemTitle>
        {line.container && (
          <ItemDescription className="tw:mb-0!">
            <Link
              to={Routes.container(line.container.id)}
              className={LINK_CLASS}
            >
              {line.container.name ??
                line.container.short_id ??
                line.container.id}
            </Link>
            {line.container.name && line.container.short_id && (
              <> ({line.container.short_id})</>
            )}
          </ItemDescription>
        )}
      </ItemContent>
      <ItemActions>
        <LineState line={line} />
        {onClear && line.active && (
          <Button size="sm" variant="secondary" onClick={() => onClear(line)}>
            {i18n.t('BrokenContainers:clearAction')}
          </Button>
        )}
      </ItemActions>
      {line.photos.length > 0 && (
        <PhotoGallery photos={line.photos} className="tw:basis-full" />
      )}
    </Item>
  );
}

function LineState({ line }: { line: BrokenContainerLineData }) {
  if (line.cleared_at) {
    return (
      <span className="tw:text-muted-foreground">
        {i18n.t('BrokenContainers:clearedBy', {
          date: formatBrokenReportDate(line.cleared_at),
          user: line.cleared_by ?? '–',
        })}
      </span>
    );
  }
  if (line.active === true) {
    return (
      <Badge variant="warning">{i18n.t('BrokenContainers:flagActive')}</Badge>
    );
  }
  if (line.active === false) {
    return (
      <Badge variant="secondary">{i18n.t('BrokenContainers:flagEnded')}</Badge>
    );
  }
  return null;
}
